import React, { FC, PropsWithChildren, ReactNode, useCallback, useState } from 'react';
import Style from './controlsGroup.module.scss';
import { ReactComponent as Plus } from 'assets/img/formBuilder/plus_24.svg';
import { ReactComponent as Minus } from 'assets/img/formBuilder/minus_24.svg';
import { ReactComponent as TooltipIcon } from 'assets/img/formBuilder/tip_16.svg';
import { AppButton } from 'uikit/buttons';
import { Tooltip } from 'uikit/tooltip/tooltip.component';
import { useEditor } from '@craftjs/core';

export type ControlsGroupProps = {
  collapsible?: boolean;
  header?: string;
  contentClassName?: string;
  tooltip?: string | React.ReactNode;
} & (
  | {
      collapsible: true;
      expanded?: boolean;
      ignoreInnerExpandedState?: boolean;
      onClose?: () => Promise<void> | void;
      onOpen?: () => Promise<void> | void;
    }
  | {
      collapsible?: false;
      expanded?: never;
      ignoreInnerExpandedState?: never;
      onClose?: never;
      onOpen?: never;
    }
);

export const ControlsGroup: FC<PropsWithChildren<ControlsGroupProps>> = ({
  header,
  expanded,
  collapsible,
  onClose,
  onOpen,
  children,
  tooltip,
  contentClassName,
  ignoreInnerExpandedState,
}) => {
  const {
    connectors: { hover },
  } = useEditor();

  const [isExpandedLocal, setIsExpanded] = useState(expanded);

  const isExpanded = isExpandedLocal || expanded;

  const clickHandler = useCallback(async () => {
    if (!ignoreInnerExpandedState) setIsExpanded(!expanded);

    if (expanded) {
      await onClose?.();
    } else {
      await onOpen?.();
    }
  }, [expanded, ignoreInnerExpandedState, onClose, onOpen]);

  return (
    <div className={Style.container}>
      {header && (
        <div className={Style.header}>
          {header}
          {collapsible && ((tooltip && isExpanded) || !tooltip) && (
            <AppButton
              variant={'icon-link'}
              colorSchema={'primary'}
              onClick={clickHandler}
              Icon={isExpanded ? Minus : Plus}
            />
          )}
          {tooltip && !isExpanded && (
            <Tooltip tooltipContent={<div className={Style.tooltipWrapper}>{tooltip}|</div>}>
              <TooltipIcon className={Style.tooltipIcon} />
            </Tooltip>
          )}
        </div>
      )}

      {(isExpanded || !collapsible) && (
        <div
          className={contentClassName}
          ref={(ref) => {
            if (ref) hover(ref, '');
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};
